import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../services/auth.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router,
    private snackbar: MatSnackBar
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Obtener el token del servicio de autenticación
    const token = this.authService.getToken();

    // Si existe token y la petición es a nuestra API, agregarlo
    if (token && this.isApiUrl(request.url)) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    // Manejar respuestas
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // 403: NO es sesión inválida (no logout). Mostrar snackbar rojo con el
        // motivo. Cubrimos DOS formatos del backend:
        //   (a) {detail: {code: 'RBAC_DENIED', msg: '...'}} — sistema permisos
        //       granulares (task #233) y require_role post-2026-09-10.
        //   (b) {detail: 'string plano'} — legacy require_role antes del fix o
        //       cualquier 403 nuevo con string. Antes caía al `else` mudo y el
        //       user no veía nada (bug reportado wizard egresos 2026-09-10).
        if (error.status === 403) {
          const d = error.error?.detail;
          const msg = d?.msg
                   ?? (typeof d === 'string' ? d : 'No tenés permisos para esta acción.');
          this.snackbar.open(msg, 'Cerrar', {
            duration: 5000,
            panelClass: 'snackbar-error',
          });
          return EMPTY;
        }

        // Si recibimos un 401 (No autorizado), hacer logout automático.
        // Caso especial: TOKEN_VERSION_MISMATCH — el JWT fue invalidado por
        // un cambio de rol o permisos aplicado por otro admin. Mostramos un
        // snackbar amber explicando la causa antes del redirect.
        if (error.status === 401) {
          const detail = String(error.error?.detail ?? '');
          if (detail.startsWith('TOKEN_VERSION_MISMATCH')) {
            this.snackbar.open(
              'Tus permisos o rol cambiaron. Iniciá sesión de nuevo.',
              'OK', { duration: 6000, panelClass: 'snackbar-error' },
            );
          }
          this.authService.logout();
          this.router.navigate(['/login']);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Verificar si la URL es de nuestra API
   */
  private isApiUrl(url: string): boolean {
    // No agregar token a URLs externas o de login
    return (
      url.includes('/api/') &&
      !url.includes('/login/alumno') &&
      !url.includes('/login/gestion')
    );
  }
}
